import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

export async function rpcCreateShift(args: {
  profile_id: string | null;
  start_iso: string;
  end_iso: string;
  note?: string | null;
}) {
  const rpcArgs: any = {
    p_start: args.start_iso,
    p_end: args.end_iso,
    p_note: args.note ?? null,
  };
  
  // Only include p_profile_id if it's not null (omit when null for TypeScript compatibility)
  if (args.profile_id) {
    rpcArgs.p_profile_id = args.profile_id;
  }
  
  const { data, error } = await supabase.rpc("create_shift", rpcArgs);
  if (error) throw error;
  return data; // shift row
}

export async function rpcClaimShift(shiftId: string) {
  const { data, error } = await supabase.rpc("claim_shift", { p_shift_id: shiftId });
  if (error) throw error;
  return data; // updated shift row
}

export async function rpcListOpenShifts(fromISO: string, toISO: string) {
  const { data, error } = await supabase.rpc("list_open_shifts", { p_start_time: fromISO, p_end_time: toISO });
  if (error) throw error;
  return data ?? [];
}

/** Unified error mapper for all shift-related RPC operations */
function baseMapShiftError(input?: string | { message?: string }) {
  const raw = typeof input === "string" ? input : input?.message;
  const m = (raw ?? "").toLowerCase();
  if (m.includes("already_claimed")) return "This shift was just claimed by someone else.";
  if (m.includes("shift_not_found")) return "That shift no longer exists.";
  if (m.includes("swaps_disabled")) return "Shift swaps are turned off for your company. Ask an admin to enable them in Business Settings.";
  if (m.includes("offers_disabled")) return "Shift offers are turned off for your company. Ask an admin to enable them in Business Settings.";
  if (m.includes("shift_not_offered")) return "This shift is no longer being offered.";
  if (m.includes("already_offered")) return "This shift has already been offered.";
  if (m.includes("invalid_swap")) return "This swap request is no longer open.";
  if (m.includes("invalid_candidate")) return "You have already responded to this swap.";
  if (m.includes("invalid_response")) return "That response isn't valid.";
  if (m.includes("invalid_shift")) return "You can only swap shifts that start in the future.";
  if (m.includes("permission_denied")) return "You don't have permission to do that.";
  if (m.includes("insufficient_permissions")) return "You don't have permission for this action.";
  if (m.includes("invalid_time_range")) return "Start time must be before end time.";
  if (m.includes("cross_tenant_forbidden")) return "You cannot modify shifts from another company.";
  if (m.includes("unauthorized")) return "You need to be signed in and active to do that.";
  if (m.includes("forbidden")) return "You don't have permission to do that.";
  if (m.includes("not_found")) return "That shift no longer exists.";
  return "Something went wrong.";
}

// Primary export - use this for all new code
export const mapShiftRpcError = baseMapShiftError;

// Backward-compatibility alias
export const mapEditShiftError = baseMapShiftError;

interface EditShiftArgs {
  shiftId: string;
  profileId: string | null;
  start: Date | null;
  end: Date | null;
  note: string | null;
}

export function useSchedulingMutations() {
  const editShift = async (args: EditShiftArgs) => {
    try {
      const rpcArgs: any = {
        p_shift_id: args.shiftId,
        p_start: args.start ? args.start.toISOString() : null,
        p_end: args.end ? args.end.toISOString() : null,
        p_note: args.note || null,
      };
      
      // Only include p_profile_id if it's not null (omit when null for TypeScript compatibility)
      if (args.profileId) {
        rpcArgs.p_profile_id = args.profileId;
      }

      console.log('[Frontend] Calling edit_shift RPC with:', rpcArgs);

      const { data, error } = await supabase.rpc('edit_shift', rpcArgs);

      if (error) {
        console.error('[Frontend] Error from edit_shift RPC:', error);
        throw error;
      }

      console.log('[Frontend] Shift edited successfully:', data);
      toast.success('Shift updated successfully');
      return data;
    } catch (error: any) {
      console.error('[Frontend] Error editing shift:', error);
      const errorMessage = mapEditShiftError(error?.message);
      toast.error(errorMessage);
      throw error;
    }
  };

  const cancelShift = async (shiftId: string) => {
    try {
      console.log('[Frontend] Calling cancel_shift RPC with:', { p_shift_id: shiftId });

      const { data, error } = await supabase.rpc('cancel_shift', { 
        p_shift_id: shiftId 
      });

      if (error) {
        console.error('[Frontend] Error from cancel_shift RPC:', error);
        throw error;
      }

      console.log('[Frontend] Shift cancelled successfully:', data);
      toast.success('Shift cancelled successfully');
      return data;
    } catch (error: any) {
      console.error('[Frontend] Error cancelling shift:', error);
      const errorMessage = mapShiftRpcError(error?.message);
      toast.error(errorMessage);
      throw error;
    }
  };

  return { 
    editShift, 
    cancelShift, 
  };
}
